import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl, slugify } from "../../lib/admin";

const empty = {
  brand_id: "",
  category_id: "",
  name: "",
  slug: "",
  images: [],
  specs: {},
  description: "",
  price: "",
  stock_status: "in_stock",
  featured: false,
  mounts: [],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [newSpecK, setNewSpecK] = useState("");
  const [newSpecV, setNewSpecV] = useState("");

  const load = () =>
    Promise.all([api.get("/products"), api.get("/brands"), api.get("/categories")]).then(([p, b, c]) => {
      setProducts(p.data);
      setBrands(b.data);
      setCats(c.data);
    });
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing("new");
    setForm({ ...empty, brand_id: brands[0]?.id || "" });
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...empty, ...p, price: p.price ?? "", mounts: p.mounts || [] });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.name),
        price: form.price === "" ? null : Number(form.price),
        category_id: form.category_id || null,
      };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const addImages = async (files) => {
    const urls = await Promise.all([...files].map(fileToDataUrl));
    setForm({ ...form, images: [...form.images, ...urls] });
  };

  const removeImage = (idx) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });
  };

  const addSpec = () => {
    if (!newSpecK) return;
    setForm({ ...form, specs: { ...form.specs, [newSpecK]: newSpecV } });
    setNewSpecK("");
    setNewSpecV("");
  };

  const removeSpec = (k) => {
    const { [k]: _, ...rest } = form.specs;
    setForm({ ...form, specs: rest });
  };

  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";
  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const filteredCats = cats.filter((c) => c.brand_id === form.brand_id);

  return (
    <div data-testid="admin-products-page">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Catalog</div>
          <h1 className="font-display text-4xl tracking-tighter">Products</h1>
        </div>
        <button onClick={openCreate} disabled={brands.length === 0} className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50" data-testid="product-create-btn">
          <Plus size={14} /> New Product
        </button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3 font-normal">Image</th>
              <th className="px-4 py-3 font-normal">Name</th>
              <th className="px-4 py-3 font-normal">Brand</th>
              <th className="px-4 py-3 font-normal">Category</th>
              <th className="px-4 py-3 font-normal">Stock</th>
              <th className="px-4 py-3 font-normal">Featured</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`product-row-${p.slug}`}>
                <td className="px-4 py-3">
                  {p.images?.[0] ? <img src={p.images[0]} className="h-10 w-10 object-contain bg-[#141414]" alt="" /> : <div className="h-10 w-10 bg-[#141414]" />}
                </td>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-zinc-400">{brandName(p.brand_id)}</td>
                <td className="px-4 py-3 text-zinc-400">{catName(p.category_id)}</td>
                <td className="px-4 py-3 text-zinc-400">{p.stock_status}</td>
                <td className="px-4 py-3 text-zinc-400">{p.featured ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-white p-2"><Edit2 size={14} /></button>
                  <button onClick={() => remove(p)} className="text-zinc-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No products yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-[#0A0A0A] border border-white/10 p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl">{editing === "new" ? "New Product" : "Edit Product"}</div>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })} className="adm-input" data-testid="product-form-name" />
              </Field>
              <Field label="Slug">
                <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="adm-input" />
              </Field>
              <Field label="Brand">
                <select required value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value, category_id: "" })} className="adm-input" data-testid="product-form-brand">
                  <option value="">Select</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select value={form.category_id || ""} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="adm-input">
                  <option value="">— None —</option>
                  {filteredCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Price (TL, optional)">
                <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="adm-input" />
              </Field>
              <Field label="Stock Status">
                <select value={form.stock_status} onChange={(e) => setForm({ ...form, stock_status: e.target.value })} className="adm-input">
                  <option value="in_stock">In Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                  <option value="coming_soon">Coming Soon</option>
                </select>
              </Field>
              <Field label="Featured" full>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                  Show on homepage
                </label>
              </Field>
              <Field label="Mounts (comma-separated, e.g. Sony E, Nikon Z, Canon RF)" full>
                <input
                  value={(form.mounts || []).join(", ")}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      mounts: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  className="adm-input"
                  placeholder="Sony E, Nikon Z, Canon RF, Panasonic L-Mount, Fujifilm X"
                  data-testid="product-form-mounts"
                />
              </Field>
              <Field label="Description" full>
                <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="adm-input" />
              </Field>

              <Field label="Images" full>
                <input type="file" accept="image/*" multiple onChange={(e) => addImages(e.target.files)} className="text-xs text-zinc-400 mb-2" data-testid="product-form-images" />
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {form.images.map((img, idx) => (
                    <div key={idx} className="relative aspect-square bg-[#141414] border border-white/10 p-2">
                      <img src={img} alt="" className="w-full h-full object-contain" />
                      <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-black/70 text-white p-1"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              </Field>

              <Field label="Specs" full>
                <div className="space-y-2 mb-3">
                  {Object.entries(form.specs).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div className="bg-[#141414] border border-white/10 px-3 py-2 text-xs uppercase tracking-[0.15em] text-zinc-400">{k}</div>
                        <div className="bg-[#141414] border border-white/10 px-3 py-2 text-zinc-200">{v}</div>
                      </div>
                      <button type="button" onClick={() => removeSpec(k)} className="text-zinc-400 hover:text-red-400 p-2"><X size={14} /></button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <input placeholder="Key (e.g. Focal Length)" value={newSpecK} onChange={(e) => setNewSpecK(e.target.value)} className="adm-input" />
                  <input placeholder="Value (e.g. 50mm)" value={newSpecV} onChange={(e) => setNewSpecV(e.target.value)} className="adm-input" />
                  <button type="button" onClick={addSpec} className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5"><Plus size={14} /></button>
                </div>
              </Field>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5">Cancel</button>
              <button type="submit" className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]" data-testid="product-form-save">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
